import { useEffect, useRef } from 'react';

/**
 * Confirmation dialog — used for destructive actions (delete staff,
 * remove photo, etc.).
 *
 * Uses a plain modal rather than <dialog> because we want consistent
 * cross-browser behaviour and custom animation. Traps focus, closes on
 * Escape, restores focus to the triggering element.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  submitting = false,
}) {
  const confirmBtnRef = useRef(null);
  const prevFocusRef = useRef(null);

  // Trap focus + restore on close
  useEffect(() => {
    if (!open) return;
    prevFocusRef.current = document.activeElement;
    // Focus the confirm button after animation settles
    const t = setTimeout(() => confirmBtnRef.current?.focus(), 50);

    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prevFocusRef.current?.focus?.();
    };
  }, [open, onCancel, submitting]);

  if (!open) return null;

  return (
    <div
      className="confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={e => {
        if (e.target === e.currentTarget && !submitting) onCancel?.();
      }}
    >
      <div className="confirm-card" role="document">
        <h2 id="confirm-title" className="confirm-title">{title}</h2>
        {body && <div className="confirm-body">{body}</div>}
        <div className="confirm-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Working…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
